/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 20 2023
 **/

import { useCallback } from "react";
import * as Hangul from "hangul-js";
import { useAtom } from "jotai";
import isEmpty from "lodash-es/isEmpty";

import { GoogleInviteStep, GoogleContactList, GoogleAccessToken, GoogleRefreshFlag } from "./atom";
import { usePeople } from "@/frameworks/core/API";
import { RegExp } from "@/frameworks/core/commons";

export default function useGoogleInvite() {
	const [googleContactList, setGoogleContactList] = useAtom(GoogleContactList);

	const [googleStep, setGoogleStep] = useAtom(GoogleInviteStep);
	const [googleAccessToken, setGoogleAccessToken] = useAtom(GoogleAccessToken);
	const [googleRefreshFlag, setGoogleRefreshFlag] = useAtom(GoogleRefreshFlag);

	const peopleAPI = usePeople();

	const googleInvite = async (credential: string) => {
		await fetch(
			`https://people.googleapis.com/v1/people/me/connections?personFields=emailAddresses,names&sortOrder=LAST_NAME_ASCENDING`,
			{
				headers: {
					"Content-Type": "application/json; charset=UTF-8",
					Authorization: `Bearer ${credential}`
				}
			}
		)
			.then((response) => response.json())
			.then((data) => {
				if (isEmpty(data)) {
					setGoogleStep("start");
					setGoogleContactList([]);
					return;
				}

				const emails = data.connections.map((item: any) => item?.emailAddresses || false);
				const names = data.connections.map((item: any) => item?.names || false);
				const map = new Map();
				names.forEach((item: any) => {
					if (!item) return;
					map.set(item[0].metadata.source.id, {
						...item[0],
						checked: false,
						consonant: RegExp.ETC_TEXT_REG.test(Hangul.disassemble(item[0].displayName)[0])
							? "#"
							: convertToConsonant(Hangul.disassemble(item[0].displayName)[0])
					});
				});

				emails.forEach((item: any) => {
					if (!item) return;
					map.set(item[0].metadata.source.id, {
						...item[0],
						...map.get(item[0].metadata.source.id)
					});
				});

				const mergedArr = Array.from(map.values()).filter((item) => Boolean(item.value));

				setGoogleContactList(
					mergedArr.map((item: any) => {
						return {
							consonant: convertToConsonant(Hangul.disassemble(item.displayName)[0]),
							username: item.value,
							name: item.displayName,
							...item
						};
					})
				);

				setGoogleStep("start");
			});
	};

	const handleToggleAllChecked = () => {
		setGoogleContactList((prev: any) => {
			const data = prev.map((item: any) => {
				return {
					...item,
					checked: true
				};
			});
			return data;
		});
	};

	const googlePeopleInvites = ({
		peopleInvites,
		type = "alone",
		id = ""
	}: {
		peopleInvites: { email: string; name: string }[];
		type?: "alone" | "all";
		id?: string;
	}) => {
		peopleAPI.invite.execute({
			peopleInvites
		});

		if (type === "all") {
			if (googleContactList.some((item) => !item.checked)) {
				handleToggleAllChecked();
			}
		} else {
			handleChecked(id);
		}
	};

	const handleChecked = useCallback((id: string) => {
		setGoogleContactList((prev: any) => {
			const data = prev.map((item: any) => {
				if (item.checked) return item;

				if (item.metadata.source.id === id) {
					return {
						...item,
						checked: true
					};
				}
				return item;
			});
			return data;
		});
	}, []);

	const handleCancel = () => {
		setGoogleStep("ready");
	};

	const handleGoogleLogout = () => {
		const googleAccount = window.google;
		if (googleAccount && googleAccessToken) {
			googleAccount.accounts.oauth2.revoke(googleAccessToken);
			setGoogleStep("ready");
			setGoogleContactList([]);
			setGoogleRefreshFlag(false);
		}
	};

	const handleGoogleContactRefresh = () => {
		try {
			setGoogleContactList([]);
			googleInvite(googleAccessToken);
		} catch (error) {
			throw Error("googleAccessToken is not found");
		}
	};

	const makeToConsonantArray = (data: any[]) => {
		const keys = data.map((item: any) => convertToConsonant(Hangul.disassemble(item.displayName)[0]));

		const uniqueKeys = deduplication(keys.map((item) => (RegExp.ETC_TEXT_REG.test(item) ? "#" : item)));
		const hangul = uniqueKeys.filter((item) => isHangul(item));

		const otherLanguage = uniqueKeys
			.filter((item) => {
				if (RegExp.ETC_TEXT_REG.test(item)) {
					return false;
				}
				return !isHangul(item);
			})
			.sort();

		const specialCharacter = uniqueKeys
			.filter((item) => {
				if (!RegExp.ETC_TEXT_REG.test(item)) {
					return false;
				}
				return !isHangul(item);
			})
			.map((item) => `${item}`)
			.sort();
		return hangul.concat(otherLanguage).concat(specialCharacter);
	};

	const deduplication = (data: string[]) => {
		return data.filter((element, index) => {
			return data.indexOf(element) === index;
		});
	};

	const isHangul = (text: string) => {
		const pattern = /^[ㄱ-ㅎㅏ-ㅣ가-힣]+$/;
		return pattern.test(text);
	};

	const convertToConsonant = (consonant: string) => {
		switch (consonant) {
			case "ㄲ":
				return "ㄱ";
			case "ㄸ":
				return "ㄷ";
			case "ㅃ":
				return "ㅂ";
			case "ㅆ":
				return "ㅅ";
			case "ㅉ":
				return "ㅈ";
			default:
				return consonant;
		}
	};

	return {
		googleStep,
		googleInvitedKeys: makeToConsonantArray(googleContactList.filter((item: any) => item.checked)),
		googleContactKeys: makeToConsonantArray(googleContactList),
		googleContactList,
		googleAccessToken,
		googleRefreshFlag,
		handlers: {
			handleCancel,
			handleChecked,
			googleInvite,
			googlePeopleInvites,
			setGoogleAccessToken,
			handleGoogleLogout,
			handleToggleAllChecked,
			handleGoogleContactRefresh,
			handleGoogleRefreshFlag: setGoogleRefreshFlag
		}
	};
}
